import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { NLIX_API_URL } from "../../config";

interface ActiveSessionStatus {
    id: number;
    user: User;
    relation: Relation;
    is_staff: boolean;
    is_admin: boolean;
    is_operator: boolean;
    is_netops: boolean;
    is_support: boolean;
    interconnect_app_enabled: boolean;
    next?: string;
}

interface LoginSsoResponse {
    url: string;
}


export interface VerifyAccessTokenSuccessResponse {
    session_validation: {
        header_name: string,
        token:  string,
        is_recovery_session: false,
        type:  string
    },
    invite: {
        invite_id:  string,
        email:  string,
        type:  string,
        resource: {
            start_url:  string,
            account_update_request_id: number

        }
    }
}

export interface User {
    id: number;
    email: string;
    username: string;
    first_name: string;
    last_name: string;
}

interface Relation {
    id: number;
    name: string;
    shortname: string;
}

interface LoginParameters {
    username: string;
    password: string;
    token?: string;
}


interface InviteParameters {
    accessCode: string;
    inviteToken: string;
}

interface PasswordResetParameters {
   email: string;
}

interface ChangePasswordParameters {
    new_password: string;
    new_password_verify: string;
    token: string;
    user: string;
    relation: string;
    otp_token?: string;
}

interface LoginSsoParameters {
    provider: string;
    successUrl: string;
    failureUrl: string;
}


export const api = createApi({
    reducerPath: "api",
    baseQuery: fetchBaseQuery({
        baseUrl: NLIX_API_URL,
        credentials: "include",
        prepareHeaders: (headers) => {
            // Add the CSRF token from the cookie set by Django as the
            // X-CSRFToken header like good ol' Django wants us to.
            const CSRFToken = getDocumentCookie("csrftoken");

            if (CSRFToken) {
                headers.append("X-CSRFToken", CSRFToken);
            }
        },
    }),
    tagTypes: ["SessionStatus"],
    endpoints: (builder) => ({
        getSessionStatus: builder.query<ActiveSessionStatus | null, void>({
            query: () => "/session/start/",
            providesTags: ["SessionStatus"],
        }),
        verifyAccessCode: builder.mutation<VerifyAccessTokenSuccessResponse, InviteParameters>({
            query: ({inviteToken, accessCode}) => ({
                url: "/invite/login/",
                method: "POST",
                body: {
                    "invite_id": inviteToken,
                    "access_token": accessCode,
                },
            }),
        }),
        loginWithCredentials: builder.mutation<ActiveSessionStatus, LoginParameters>({
            query: ({ username, password, token }) => ({
                url: "/auth/login/",
                method: "POST",
                body: {
                    username,
                    password,
                    token: token || undefined,
                },
            }),
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                // update getSessionStatus cache with login result when successful
                try {
                    const { data: sessionStatus } = await queryFulfilled;

                    dispatch(api.util.upsertQueryData("getSessionStatus", undefined, sessionStatus));
                } catch (e) {
                    // no need to do anything; login failed
                }
            },
        }),
        logout: builder.mutation<void, void>({
            query: () => ({
                url: "/auth/logout/",
                method: "POST",
                body: {},
            }),
            onQueryStarted: async (_, { dispatch }) => {
                // clear out session status, regardless of whether the API calls succeeds
                dispatch(api.util.upsertQueryData("getSessionStatus", undefined, null));
            },
        }),
        loginSso: builder.mutation<LoginSsoResponse, LoginSsoParameters>({
            query: ({ provider, successUrl, failureUrl }) => ({
                url: "/auth/login-sso/",
                method: "POST",
                body: {
                    provider,
                    success_url: successUrl,
                    failure_url: failureUrl,
                },
            }),
        }),
        passwordReset: builder.mutation<null, PasswordResetParameters>({
            query: ({email}) => ({
                url: "/auth/password_reset/",
                method: "POST",
                body: {
                    email
                },
            })
        }),
        changePassword: builder.mutation<null, ChangePasswordParameters>({
            query: ({ new_password, new_password_verify, token, otp_token, user, relation }) => ({
                url: `/relation/${encodeURIComponent(relation)}/users/${encodeURIComponent(user)}/complete_password_reset/`,
                method: "PUT",
                body: {
                    new_password,
                    new_password_verify,
                    token,
                    otp_token,
                },
            })
        }),
    }),
});

function getDocumentCookies() {
    return new Map(document.cookie.split("; ").map((cookie) => cookie.split("=", 2) as [string, string]));
}

function getDocumentCookie(name: string) {
    return getDocumentCookies().get(name);
}

export const {
    useGetSessionStatusQuery,
    useLoginWithCredentialsMutation,
    useLogoutMutation,
    useLoginSsoMutation,
    usePasswordResetMutation,
    useChangePasswordMutation,
    useVerifyAccessCodeMutation,
} = api;
export default api;
